// form
import { Controller, useFormContext } from 'react-hook-form';
// @mui
import { Autocomplete, AutocompleteProps, TextField } from '@mui/material';

// ----------------------------------------------------------------------

interface Props<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
> extends AutocompleteProps<T, Multiple, DisableClearable, FreeSolo> {
  name: string;
  label?: string;
  helperText?: React.ReactNode;
  required?: boolean;
  inputProps?: any;
}

export default function RHFAutocomplete<
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
>({
  name,
  label,
  helperText,
  onChange,
  required = false,
  inputProps,
  ...other
}: Omit<Props<T, Multiple, DisableClearable, FreeSolo>, 'renderInput'>) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({ field, fieldState: { error } }) => {
        return (
          <Autocomplete
            {...field}
            onChange={(event, newValue) => {
              // If a custom onChange is provided, use it to transform the value
              if (onChange) {
                // @ts-ignore - Ignoring type mismatch as we know this is safe
                const transformedValue = onChange(event, newValue);
                // Update the form with the transformed value
                field.onChange(transformedValue);
              } else {
                // If no custom onChange, just update with the raw value
                field.onChange(newValue);
              }
            }}
            renderInput={(params) => (
              <TextField
                label={label}
                required={required}
                error={!!error}
                helperText={error ? error?.message : helperText}
                {...params}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: inputProps?.startAdornment,
                }}
              />
            )}
            {...other}
          />
        );
      }}
    />
  );
}
